// src/components/DdosTesting/AttackTypeSelector.jsx
import React from 'react';
import { motion } from 'framer-motion';

// Added isDisabled prop
const AttackTypeSelector = ({ selectedType, onTypeChange, isDisabled }) => {
  const attackTypes = [
    // ... (attackTypes array remains the same)
      {
        id: 'bot',
        title: 'Bot DDoS Attack',
        description: 'Multiple bot clients connect to overload server capacity',
        icon: 'fa-robot',
        color: 'red',
        stats: [
          { label: 'Effectiveness', value: '75%', color: 'red' },
          { label: 'Detection Risk', value: '50%', color: 'yellow' },
          { label: 'Resource Usage', value: '80%', color: 'red' }
        ]
      },
      {
        id: 'packet',
        title: 'Packet DDoS Attack',
        description: 'Send large volumes of malformed packets to overload network',
        icon: 'fa-network-wired',
        color: 'purple',
        stats: [
          { label: 'Effectiveness', value: '100%', color: 'red' },
          { label: 'Detection Risk', value: '80%', color: 'red' },
          { label: 'Resource Usage', value: '50%', color: 'yellow' }
        ]
      }
  ];

  return (
    <div className={`mb-8 transform-gpu transition-medium hover:scale-[1.005] ${isDisabled ? 'opacity-60 pointer-events-none' : ''}`}>
      <h2 className="text-2xl font-bold mb-5 flex items-center group">
         {/* ... (header content remains the same) ... */}
         <div className="w-10 h-10 rounded-lg bg-accent-red/20 flex items-center justify-center mr-3 transform-gpu group-hover:rotate-12 transition-medium">
            <i className="fas fa-crosshairs text-accent-red"></i>
         </div>
         Attack Type Selection
         <div className="ml-3 text-xs py-1 px-2 bg-accent-red/20 rounded-full text-accent-red opacity-0 group-hover:opacity-100 transition-medium">
            2 methods available
         </div>
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {attackTypes.map((type) => (
          <motion.div // Wrap with motion for potential future animations
            key={type.id}
            className={`bg-dark-600 gradient-bg-${type.color} rounded-2xl p-6 hover:shadow-lg transition-medium gradient-border relative overflow-hidden transform-gpu hover:-translate-y-1 hover:shadow-glow active:translate-y-0 active:shadow-inner ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            onClick={() => !isDisabled && onTypeChange(type.id)} // Prevent click when disabled
          >
             {/* ... (rest of the card content remains the same) ... */}
             {/* Background glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-accent-pink/10 to-accent-purple/10 rounded-xl blur opacity-0 group-hover:opacity-100 transition-medium"></div>

            <div className="flex items-center mb-4 relative z-10">
              <div className={`w-12 h-12 rounded-xl bg-accent-${type.color} bg-opacity-20 flex items-center justify-center mr-4 transition-medium transform-gpu group-hover:scale-110 group-hover:rotate-3`}>
                <i className={`fas ${type.icon} text-accent-${type.color} text-xl`}></i>
              </div>
              <div>
                <h3 className="text-lg font-bold group-hover:text-white transition-medium">{type.title}</h3>
                <p className="text-sm text-gray-400 group-hover:text-gray-300 transition-medium">{type.description}</p>
              </div>
            </div>
            <div className="space-y-4 relative z-10">
              {type.stats.map((stat, index) => (
                <div key={index} className="flex justify-between text-sm group-hover:scale-105 transition-medium">
                  <span className="text-gray-400 group-hover:text-white transition-medium">{stat.label}:</span>
                  <div className="flex items-center">
                    <div className="w-20 h-2 bg-dark-400 rounded-full overflow-hidden mr-2">
                      <div className={`h-full bg-accent-${stat.color} transition-all duration-500`} style={{ width: stat.value }}></div>
                    </div>
                    <span className={`text-accent-${stat.color} group-hover:opacity-100 opacity-90 transition-medium`}>
                      {stat.value === '100%' ? 'Very High' : stat.value === '50%' ? 'Medium' : 'High'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="absolute top-4 right-4 transition-medium transform-gpu group-hover:scale-110">
              <div className={`w-6 h-6 rounded-full border-2 border-accent-${type.color} flex items-center justify-center ${selectedType === type.id ? 'bg-accent-' + type.color + '/20' : ''}`}>
                {selectedType === type.id && (
                  <motion.div // Animate the checkmark or inner dot
                    layoutId="ddos-type-selector"
                    className={`w-3 h-3 rounded-full bg-accent-${type.color}`}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                  />
                )}
              </div>
            </div>

            {/* Corner flourish */}
            <div className="absolute -bottom-4 -right-4 w-16 h-16 opacity-10 bg-white rounded-full blur-xl group-hover:opacity-20 transition-medium"></div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default AttackTypeSelector;